import * as React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    SelectChangeEvent,
} from '@mui/material';
import { Close, Save } from '@mui/icons-material';

/**
 * Props for the SelectVerificationDialog component
 */
interface SelectVerificationDialogProps {
    /** Whether the dialog is open */
    open: boolean;
    /** The mode ('status' or 'verification') */
    mode: string;
    /** Number of selected items */
    selectedCount: number;
    /** Callback when dialog is closed */
    onClose: () => void;
    /** Callback when save action is triggered with mode and status value */
    onSave: (mode: string, statusValue: number) => void;
    /** Type of entity ('Categories' or 'Performers') for display text */
    entityType: string;
}

/**
 * Shared Select Verification Dialog Component
 * Confirmation dialog for bulk verification status updates
 * Shows the number of selected items and confirmation for the action
 *
 * When mode === 'status': Displays dual-button interface for Active/InActive selection
 * When mode !== 'status': Displays dropdown with Not verified/Whitelist/Blacklist options and single confirm button
 */
const SelectVerificationDialog: React.FC<SelectVerificationDialogProps> = ({
    open,
    mode,
    selectedCount,
    onClose,
    onSave,
    entityType
}) => {
    /**
     * State to track the currently selected verification option in dropdown mode
     * Default value is "Not verified"
     */
    const [selectedVerification, setSelectedVerification] = React.useState<string>('Not verified');

    // Return if not open
    if (!open) return null;

    /**
     * Handles saving the selected verification status
     * Maps text values to numeric status values based on mode
     *
     * @param text - The selected text option
     */
    const handleSave = (text: string): void => {
        let statusValue: number;
        if (mode === 'status') {
            // Status mode: Active → 1, InActive → 0
            statusValue = text === 'Active' ? 1 : 0;
        } else {
            // Verification mode: Not verified → 0, Whitelist → 1, Blacklist → 2
            statusValue = text === 'Whitelist' ? 1 : text === 'Blacklist' ? 2 : 0;
        }

        onSave(mode, statusValue);
    };

    /**
     * Handles dropdown selection change
     * Updates the selected verification option state
     *
     * @param event - The select change event
     */
    const handleVerificationChange = (event: SelectChangeEvent<string>): void => {
        setSelectedVerification(event.target.value);
    };

    const handleClose = (): void => {
        onClose();
    };

    const title = mode === 'status'
        ? `Set Status for Selected ${entityType}`
        : `Set Verification for Selected ${entityType}`;

    const statusText: string[] = ['Active', 'InActive'];
    const actionColor: Array<'success' | 'error'> = ['success', 'error'];

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                <Box sx={{ py: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        This action will update <strong>{selectedCount}</strong> selected records in a single operation.
                    </Typography>
                    
                    {/* Show dropdown for verification mode only */}
                    {mode !== 'status' && (
                        <Box sx={{ mt: 3 }}>
                            <FormControl fullWidth variant="outlined" size="small">
                                <InputLabel id="verification-select-label">Verification Status</InputLabel>
                                <Select
                                    labelId="verification-select-label"
                                    id="verification-select"
                                    value={selectedVerification}
                                    onChange={handleVerificationChange}
                                    label="Verification Status"
                                >
                                    <MenuItem value="Not verified">Not verified</MenuItem>
                                    <MenuItem value="Whitelist">Whitelist</MenuItem>
                                    <MenuItem value="Blacklist">Blacklist</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} startIcon={<Close />}>
                    Cancel
                </Button>
                
                {/* Conditional rendering based on mode */}
                {mode === 'status' ? (
                    // Status mode: Show dual buttons for Active/InActive
                    <>
                        <Button
                            onClick={() => handleSave(statusText[1])}
                            startIcon={<Save />}
                            color={actionColor[1]}
                            variant="contained"
                        >
                            Confirm {statusText[1]}
                        </Button>
                        <Button
                            onClick={() => handleSave(statusText[0])}
                            startIcon={<Save />}
                            color={actionColor[0]}
                            variant="contained"
                        >
                            Confirm {statusText[0]}
                        </Button>
                    </>
                ) : (
                    // Verification mode: Show single confirm button
                    <Button
                        onClick={() => handleSave(selectedVerification)}
                        startIcon={<Save />}
                        color="primary"
                        variant="contained"
                    >
                        Confirm
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default SelectVerificationDialog;
